/** Training videos (shell route `/training`): catalog list + pop-up player. `?video=<id>` deep-links one video. */
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authApi, type TrainingVideo } from '../services/authApi';
import { useTab } from '../shell/TabContext';
import { VideoDialog } from '../shell/home/VideoDialog';

type ListError = 'unavailable' | 'error';

const TrainingPage: React.FC = () => {
  const { isActive, rootRef } = useTab();
  const [searchParams, setSearchParams] = useSearchParams();
  const selected = searchParams.get('video');
  const [list, setList] = useState<TrainingVideo[] | null>(null);
  const [listError, setListError] = useState<ListError | null>(null);
  const [embed, setEmbed] = useState<{ id: string; url: string } | null>(null);
  const [embedError, setEmbedError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    authApi
      .getTrainingVideos()
      .then((v) => { if (!cancelled) setList(v); })
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = (e as { response?: { status?: number } })?.response?.status;
        setListError(status === 503 ? 'unavailable' : 'error');
      });
    return () => { cancelled = true; };
  }, []);

  // A fresh signed URL per activation/selection; a response arriving after the
  // selection changed or the tab deactivated is never mounted.
  useEffect(() => {
    if (!isActive || !selected || !list || !list.some((v) => v.id === selected)) return;
    let cancelled = false;
    authApi
      .getTrainingEmbedUrl(selected)
      .then((url) => { if (!cancelled) setEmbed({ id: selected, url }); })
      .catch(() => {
        if (cancelled) return;
        setEmbedError('Could not load the video. Please try again.');
        setSearchParams({}, { replace: true });
      });
    return () => {
      cancelled = true;
      setEmbed(null);
    };
  }, [selected, isActive, list, setSearchParams]);

  const open = (id: string) => {
    setEmbedError(null);
    setSearchParams({ video: id }, { replace: true });
  };
  const close = () => {
    const id = selected;
    setSearchParams({}, { replace: true });
    // Scoped to this tab's pane: other (hidden) Training tabs have the same cards.
    if (id) (rootRef.current ?? document).querySelector<HTMLButtonElement>(`[data-testid="training-card-${CSS.escape(id)}"]`)?.focus();
  };

  const video = embed && embed.id === selected ? list?.find((v) => v.id === selected) : undefined;

  return (
    <div data-testid="training-page" className="mx-auto w-full max-w-3xl p-8">
      <h1 className="font-display text-[28px] font-medium tracking-[-0.02em] text-ink">Training</h1>
      {embedError && (
        <p role="alert" className="mt-4 text-[13px] text-signal">{embedError}</p>
      )}
      {listError === 'unavailable' && <p className="mt-4 text-[13px] text-ink-3">Training videos are unavailable right now</p>}
      {listError === 'error' && <p className="mt-4 text-[13px] text-ink-3">Could not load training videos. Please try again later.</p>}
      {!list && !listError && <p className="mt-4 text-[13px] text-ink-3">Loading…</p>}
      {list && list.length === 0 && <p className="mt-4 text-[13px] text-ink-3">No training videos yet.</p>}
      {list && list.length > 0 && (
        <ul className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {list.map((v) => (
            <li key={v.id}>
              <button
                type="button"
                data-testid={`training-card-${v.id}`}
                onClick={() => open(v.id)}
                className="flex w-full flex-col gap-2 rounded border border-line bg-white p-3 text-left hover:bg-black/5"
              >
                <span className="relative block aspect-video w-full overflow-hidden rounded bg-black/10">
                  <img src={v.thumbnail_url} alt="" loading="lazy" className="h-full w-full object-cover" />
                  <span className="absolute bottom-1 right-1 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[11px] text-white">
                    {v.duration}
                  </span>
                </span>
                <span className="font-mono text-[11px] tracking-[0.1em] text-ink-3">{v.topic}</span>
                <span className="font-display text-[17px] font-medium leading-[1.3] text-ink">{v.title}</span>
                <span className="text-[13px] leading-[1.5] text-ink-3">{v.description}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {video && embed && <VideoDialog embedUrl={embed.url} title={video.title} onClose={close} />}
    </div>
  );
};

export default TrainingPage;
